<template>
  <v-container id="location-details" fluid>
    <v-card outlined>
      <v-container v-if="Object.keys(loadedSettings).length" fluid>
        <v-row>
          <v-col cols="12" sm="6">
            <v-switch
              v-model="loadedSettings.displayParcelDeliveryButton"
              :label="$t('settings.displayParcelDeliveryButton')"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-switch
              v-model="loadedSettings.displayEbookingDeliveryButton"
              :label="$t('settings.displayEbookingDeliveryButton')"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-switch
              v-model="loadedSettings.displayDeliverPackageToTransporterButton"
              :label="$t('settings.displayDeliverPackageToTransporterButton')"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-switch
              v-model="loadedSettings.displayFastPreparationButton"
              :label="$t('settings.displayFastPreparationButton')"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-switch
              v-model="loadedSettings.displayStockIndicatorOnProduct"
              :label="$t('settings.displayStockIndicatorOnProduct')"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-switch
              v-model="loadedSettings.displayOrderToPutBackInStock"
              :label="$t('settings.displayOrderToPutBackInStock')"
              :messages="$t('settings.activateOrderToPutBackInStockMessage')"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="loadedSettings.displayAddressEditButtons"
              :items="roles"
              :label="$t('settings.displayAddressEditButtons')"
              multiple
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="loadedSettings.displayCustomerEditButtons"
              :items="roles"
              :label="$t('settings.displayCustomerEditButtons')"
              multiple
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="loadedSettings.displayOrdersGlobalSearch"
              :items="shopLevelRoles"
              :label="$t('settings.displayOrdersGlobalSearch')"
              multiple
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="loadedSettings.displayForceDelivered"
              :items="highPrivilegeRoles"
              :label="$t('settings.displayForceDelivered')"
              multiple
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="loadedSettings.displayRetryBlockedOrdersButton"
              :items="highPrivilegeRoles"
              :label="$t('settings.displayRetryBlockedOrdersButton')"
              :messages="$t('settings.displayRetryBlockedOrdersButtonMessage')"
              multiple
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="loadedSettings.displayPickupDeadlineExtensionButton"
              :items="roles"
              :label="$t('settings.displayPickupDeadlineExtensionButton')"
              :messages="$t('settings.displayPickupDeadlineExtensionButtonMessage')"
              multiple
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="loadedSettings.displayEbookingPickupDeadlineExtensionButton"
              :items="roles"
              :label="$t('settings.displayEbookingPickupDeadlineExtensionButton')"
              :messages="$t('settings.displayEbookingPickupDeadlineExtensionButtonMessage')"
              multiple
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="loadedSettings.displayWaitingPaymentOrdersMenu"
              :items="shopLevelRoles"
              :label="$t('settings.displayWaitingPaymentOrdersMenu')"
              multiple
            ></v-select>
          </v-col>
          <!-- Empty column to force a line break -->
          <v-col class="d-none d-sm-block" cols="12" sm="6"></v-col>
          <v-col cols="12">
            <v-divider></v-divider>
          </v-col>
          <v-col cols="12" sm="6">
            <validation-provider v-slot="{ errors }" :name="$t('settings.itemCancelReasons')" rules="required">
              <v-autocomplete
                v-model="loadedSettings.itemCancelReasons"
                :error-messages="errors"
                :items="itemsCancelReasons"
                :label="$t('settings.itemCancelReasons')"
                :messages="$t('settings.itemCancelReasonsMessage')"
                multiple
              >
                <template v-slot:selection="{ item, index }">
                  <span v-if="index === 0">{{ item.text }}</span>
                  <span v-if="index === 1" class="grey--text caption ml-1">
                    (+{{ loadedSettings.itemCancelReasons.length - 1 }} {{ $t('settings.others') }})
                  </span>
                </template>
              </v-autocomplete>
            </validation-provider>
          </v-col>
          <v-col cols="12" sm="6">
            <v-switch
              v-model="loadedSettings.displayProductsCustomCancelReason"
              :label="$t('settings.displayProductsCustomCancelReason')"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <validation-provider v-slot="{ errors }" :name="$t('settings.orderCancelReasons')" rules="required">
              <v-autocomplete
                v-model="loadedSettings.orderCancelReasons"
                :error-messages="errors"
                :items="cancelReasons"
                :label="$t('settings.orderCancelReasons')"
                :messages="$t('settings.orderCancelReasonsMessage')"
                multiple
              >
                <template v-slot:selection="{ item, index }">
                  <span v-if="index === 0">{{ item.text }}</span>
                  <span v-if="index === 1" class="grey--text caption ml-1">
                    (+{{ loadedSettings.orderCancelReasons.length - 1 }} {{ $t('settings.others') }})
                  </span>
                </template>
              </v-autocomplete>
            </validation-provider>
          </v-col>
          <v-col cols="12" sm="6">
            <v-switch
              v-model="loadedSettings.displayCustomCancelReason"
              :label="$t('settings.displayCustomCancelReason')"
            />
          </v-col>
        </v-row>
      </v-container>
    </v-card>
    <v-card v-if="loadedSettings.customerInformation" class="mt-2 pa-2" outlined>
      <v-card-title>{{ $t('settings.restrictCustomerInformation') }}</v-card-title>
      <v-row>
        <v-col cols="12" lg="6" md="6">
          <v-select
            v-model="loadedSettings.customerInformation.customerName"
            :items="roles"
            :label="$t('settings.customerName')"
            multiple
          ></v-select>
        </v-col>
        <v-col cols="12" lg="6" md="6">
          <v-select
            v-model="loadedSettings.customerInformation.customerEmail"
            :items="roles"
            :label="$t('settings.customerEmail')"
            multiple
          ></v-select>
        </v-col>
        <v-col cols="12" lg="6" md="6">
          <v-select
            v-model="loadedSettings.customerInformation.addresses"
            :items="roles"
            :label="$t('settings.address')"
            multiple
          ></v-select>
        </v-col>
        <v-col cols="12" lg="6" md="6">
          <v-select
            v-model="loadedSettings.customerInformation.fullPhoneNumber"
            :items="roles"
            :label="$t('settings.fullPhoneNumber')"
            multiple
          ></v-select>
        </v-col>
        <v-col cols="12" lg="6" md="6">
          <v-select
            v-model="loadedSettings.customerInformation.lastFourDigitPhoneNumber"
            :items="roles"
            :label="$t('settings.lastFourDigitPhone')"
            multiple
          ></v-select>
        </v-col>
      </v-row>
    </v-card>
    <order-visibility-settings
      v-if="isOctipasAdmin && loadedSettings.ordersVisibilityConditions"
      :value="loadedSettings.ordersVisibilityConditions"
      :default-conditions="loadedSettings.ordersVisibilityDefaultConditions || []"
      :error-key="loadedSettings.ordersVisibilityErrorKey || ''"
      @input="onOrdersVisibilityInput"
    />
  </v-container>
</template>

<script>
  import { mapState } from 'vuex'
  import { ValidationProvider, extend } from 'vee-validate'
  import { required } from 'vee-validate/dist/rules'
  import OrderVisibilitySettings from './OrderVisibilitySettings.vue'
  import { shopLevelRoleOptions } from '@/helpers/pickupCancelPermission'

  extend('required', { ...required })
  export default {
    name: 'DisplaySettings',
    components: {
      'validation-provider': ValidationProvider,
      orderVisibilitySettings: OrderVisibilitySettings,
    },
    props: {
      loadedSettings: {
        type: Object,
        min: false,
      },
      loading: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      ...mapState(['isOctipasAdmin']),
      roles() {
        return [
          { text: this.$t('users.seller'), value: 'default-user' },
          { text: this.$t('users.shopManager'), value: 'oms-shop-manager' },
          { text: this.$t('users.customerService'), value: 'oms-customer-service' },
          { text: this.$t('users.admin'), value: 'oms-admin' },
        ]
      },
      shopLevelRoles() {
        return shopLevelRoleOptions((key) => this.$t(key))
      },
      highPrivilegeRoles() {
        return [
          { text: this.$t('users.customerService'), value: 'oms-customer-service' },
          { text: this.$t('users.admin'), value: 'oms-admin' },
        ]
      },
      cancelReasons() {
        return [
          { text: this.$t('subOrder.clientChangeOfMind'), value: 'client_changed_his_mind' },
          { text: this.$t('subOrder.deterioratedProducts'), value: 'deteriorated_products' },
          { text: this.$t('subOrder.orderedMultipleTimes'), value: 'ordered_multiple_times' },
          { text: this.$t('subOrder.productsNotAvailable'), value: 'products_not_available' },
          { text: this.$t('subOrder.customerDidNotCollect'), value: 'customer_did_not_collect_order' },
        ]
      },
      itemsCancelReasons() {
        return [
          { text: this.$t('subOrder.notAvailable'), value: 'not_available' },
          { text: this.$t('subOrder.defective'), value: 'defective' },
          { text: this.$t('subOrder.alreadyReserved'), value: 'already_reserved' },
        ]
      },
    },
    methods: {
      validateForm() {
        return this.$refs.form.validate()
      },
      onOrdersVisibilityInput(conditions) {
        this.loadedSettings.ordersVisibilityConditions = conditions
        // Clear any stale business-validation error as soon as the admin edits the block.
        this.loadedSettings.ordersVisibilityErrorKey = ''
      },
    },
  }
</script>
